import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import { Button } from '../components/Common/Button'
import { Loader } from '../components/Common/Loader'
import {
  GlobalActionLoadingContext,
  type GlobalActionLoadingContextValue,
  type GlobalActionLoadingOptions,
} from './globalActionLoadingContextCore'

const DEFAULT_DELAY_MS = 300
const DEFAULT_TIMEOUT_MS = 15_000
const MESSAGE_ROTATE_MS = 2_800

const DEFAULT_MESSAGES = [
  'Aligning the workspace...',
  'Opening the right screen...',
  'Syncing the latest changes...',
  'Checking the action queue...',
  'Keeping this action protected...',
]

type ActiveAction = Required<Pick<GlobalActionLoadingOptions, 'delayMs' | 'timeoutMs'>> & {
  id: string
  controller: AbortController
  label: string
  messages: string[]
  visible: boolean
  timedOut: boolean
}

const newActionId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `global-action-${Date.now()}-${Math.round(Math.random() * 1e6)}`

const normalizeOptions = (options?: GlobalActionLoadingOptions) => ({
  label: options?.label?.trim() || 'Working',
  messages: options?.messages?.length ? options.messages : DEFAULT_MESSAGES,
  delayMs: options?.delayMs ?? DEFAULT_DELAY_MS,
  timeoutMs: options?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
})

export const GlobalActionLoadingProvider = ({ children }: PropsWithChildren) => {
  const [activeAction, setActiveAction] = useState<ActiveAction | null>(null)
  const [messageIndex, setMessageIndex] = useState(0)
  const activeIdRef = useRef<string | null>(null)

  const finishAction = useCallback((id: string) => {
    if (activeIdRef.current !== id) return
    activeIdRef.current = null
    setActiveAction(null)
    setMessageIndex(0)
  }, [])

  const cancelAction = useCallback(() => {
    setActiveAction((current) => {
      if (!current) return null
      current.controller.abort()
      activeIdRef.current = null
      return null
    })
    setMessageIndex(0)
  }, [])

  const runWithGlobalLoading = useCallback<GlobalActionLoadingContextValue['runWithGlobalLoading']>(
    async (action, options) => {
      if (activeIdRef.current) return undefined
      const id = newActionId()
      const controller = new AbortController()
      const normalized = normalizeOptions(options)
      activeIdRef.current = id
      setMessageIndex(0)
      setActiveAction({
        ...normalized,
        id,
        controller,
        visible: false,
        timedOut: false,
      })

      try {
        return await action(controller.signal)
      } finally {
        finishAction(id)
      }
    },
    [finishAction],
  )

  useEffect(() => {
    if (!activeAction) return
    const delayTimer = window.setTimeout(() => {
      setActiveAction((current) => current?.id === activeAction.id ? { ...current, visible: true } : current)
    }, activeAction.delayMs)
    const timeoutTimer = window.setTimeout(() => {
      setActiveAction((current) => current?.id === activeAction.id ? { ...current, visible: true, timedOut: true } : current)
    }, activeAction.timeoutMs)
    return () => {
      window.clearTimeout(delayTimer)
      window.clearTimeout(timeoutTimer)
    }
  }, [activeAction?.id, activeAction?.delayMs, activeAction?.timeoutMs])

  useEffect(() => {
    if (!activeAction?.visible) return
    const rotateTimer = window.setInterval(() => {
      setMessageIndex((current) => (current + 1) % activeAction.messages.length)
    }, MESSAGE_ROTATE_MS)
    return () => window.clearInterval(rotateTimer)
  }, [activeAction?.visible, activeAction?.messages.length])

  const value = useMemo<GlobalActionLoadingContextValue>(
    () => ({
      isActionLoading: Boolean(activeAction),
      runWithGlobalLoading,
    }),
    [activeAction, runWithGlobalLoading],
  )

  const message = activeAction?.messages[messageIndex % activeAction.messages.length] ?? DEFAULT_MESSAGES[0]

  return (
    <GlobalActionLoadingContext.Provider value={value}>
      {children}
      {activeAction?.visible ? (
        <div className="globalActionOverlay" role="alertdialog" aria-modal="true" aria-label={activeAction.label}>
          <div className="globalActionOverlay__panel">
            <Loader size={360} ariaLabel={activeAction.label} />
            <div className="globalActionOverlay__copy">
              <span className="settingsEyebrow">{activeAction.label}</span>
              <p>{message}</p>
              {activeAction.timedOut ? (
                <span className="globalActionOverlay__timeout">
                  This is taking longer than expected. You can cancel the wait and try again.
                </span>
              ) : null}
            </div>
            {activeAction.timedOut ? (
              <Button variant="secondary" size="sm" onClick={cancelAction}>
                Cancel operation
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </GlobalActionLoadingContext.Provider>
  )
}
